// ─────────────────────────────────────────────
//  AssignmentImport — AIC upload + draft-review + explicit Apply
//
//  Two import sources, ONE review/Apply pipeline:
//    • Excel file upload (.xlsx/.xls) — parsed in-browser via SheetJS.
//    • Google Sheet — read live via OAuth (Drive title lookup).
//  Both produce the raw 2D `values` array → parsePrelimAssignments →
//  toSheetDraftGrid → reviewable DRAFT. No AI, no Cloud Function, no API key.
//
//  Guardrails:
//    - Nothing writes to Firestore until the AIC clicks "Confirm / Apply to board"
//    - Zero facility rooms → blocked (not presented as success)
//    - Parse / read failure → show error, keep the manual grid
//    - Unmatched short codes + low-confidence fields → per-field flag badges
//    - AIC-tag count != 1 → grid-level warning
// ─────────────────────────────────────────────
import React, { useMemo, useRef, useState } from "react";
import tokens from "../theme/tokens";
import Button from "./Button";
import Card from "./Card";
import { readExcelValues } from "../features/sheet-import/excelClient";
import { importSheetValues } from "../features/sheet-import/sheetsClient";
import { parsePrelimAssignments } from "../features/sheet-import/parsePrelimAssignments";
import { parseAttendingCallList } from "../features/sheet-import/attendingCallList";
import { toSheetDraftGrid } from "../features/sheet-import/toSheetDraftGrid";
import ImportReviewQueue from "./ImportReviewQueue";
import useImportReviewActions, { applyBlockedBy } from "../hooks/useImportReviewActions";
import { facilityCoverage } from "../features/sheet-import/importCoverage";
import { parseQgendaIdentity, qgendaDisplayIndex } from "../features/sheet-import/qgendaIdentity";
import { listStaffAliases } from "../firebase/firestore";
import DraftRow from "./DraftRow";
import st from "./assignmentImportStyles";
import { facilityLabel } from "../domain/facilities";
import { importFilename, relativeImportTime, sourceLabel } from "../domain/sheetImports";
import { roomFamilyForFacility } from "../domain/sheetRoomFamily";
import { useFacility } from "../hooks/useFacility";

const { space } = tokens;

// Max upload size — Excel sheets are tiny; generous cap as a safety guard.
const MAX_BYTES = 32 * 1024 * 1024; // 32 MB
// Accepted spreadsheet extensions (Excel + CSV exports of the daily sheet).
const EXCEL_EXT_RE = /\.(xlsx|xls|xlsm|csv)$/i;

// ── State machine ─────────────────────────────
// "idle" | "parsing" | "review" | "applying"
const IDLE = "idle";
const PARSING = "parsing";
const REVIEW = "review";
const APPLYING = "applying";

// ── Main component ─────────────────────────────
export default function AssignmentImport({
  facilityId,
  date,
  doctors = [],
  crnas = [],
  rooms = [],
  // Full staff pools (all doctors/CRNAs, not just today's roster). The Google
  // Sheet lists attendings by short code and CRNAs by name regardless of who is
  // rostered today, so sheet matching must run against the whole pool — matching
  // only against the roster skipped almost every row ("unknown MD code"). The
  // PDF path keeps using the roster-scoped doctors/crnas. Fall back to the
  // roster props when full pools are not supplied.
  allDoctors = [],
  allCrnas = [],
  onApplyRows,
  // ⚠️ THERE IS NO `onAddStaff` HERE ANY MORE. This component used to create a
  // staff document for every name the sheet wrote that the roster did not
  // carry — 22 of them on the live sheet of 2026-08-20, 13 of which were
  // colleagues already on file under their legal name. Creating a person is now
  // one deliberate two-click act inside the review queue below, and it is the
  // only door: no code path from "the importer ran" reaches a staff write.
  // Recent import history (newest first, already sliced to 3 by the parent),
  // each { id, filename, source, importedAt, snapshot }. Rendered below the
  // pickers; clicking one restores its draft for review + re-apply.
  recentImports = [],
  // Called after a successful Apply with { filename, source, snapshot } so the
  // parent can persist the import to the recent-files history. Optional.
  onSaveImport,
}) {
  const [phase, setPhase] = useState(IDLE);
  const [error, setError] = useState("");
  const [draftRows, setDraftRows] = useState([]);
  const [gridFlags, setGridFlags] = useState([]);
  const [skipped, setSkipped] = useState([]);
  // CRNAs the sheet parked on a TBD room → unassigned bench (applied to roster).
  const [unassigned, setUnassigned] = useState([]);
  // Facility-wide MD call list parsed from the sheet's attending column.
  const [mdCall, setMdCall] = useState([]);
  // Sheet-import re-resolve state: the raw parsed rows + the room ctx so Apply
  // can re-run toSheetDraftGrid against augmented pools after adding new staff.
  const [parsedRows, setParsedRows] = useState([]);
  const [sheetRooms, setSheetRooms] = useState([]);
  // What this import understood and what it needs a person for — built by the
  // parser, rendered before Apply, and the gate on the Apply button.
  const [coverage, setCoverage] = useState(null);
  // Answers humans already gave (staff_aliases), read before the ladder runs.
  const [aliases, setAliases] = useState([]);
  const [qgenda, setQgenda] = useState(null);
  const {
    answered, acknowledged, setAcknowledged, reset: resetReview,
    confirmAlias, createPerson, skipItem,
  } = useImportReviewActions();
  // Origin of the current draft — { filename, source } — captured at ingest so a
  // successful Apply can save it to the recent-imports history.
  const [sourceMeta, setSourceMeta] = useState({ filename: "", source: "manual-excel" });
  const fileRef = useRef(null);
  // Manual grid edits keyed by row index → { field: value }. `draftRows` is
  // already the edited grid (handleCellChange writes both), so Apply sends what
  // the AIC sees; this ref survives only so a future re-resolve can re-apply
  // them rather than silently reverting a correction.
  const editedCellsRef = useRef({});
  const { facility } = useFacility(facilityId);

  // ⚠️ THE WHOLE ROSTER, so the review queue can bind a cell to somebody the
  // ladder never proposed. "Liz B" matched nobody — the one-letter surname is
  // dropped before the surname lookup — so the queue could only offer Skip or
  // "add as a new person", and adding would have minted a second phantom
  // beside the Elizabeth Bennett already on file.
  const reviewStaffOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const p of [...(allCrnas || []), ...(allDoctors || []), ...(crnas || []), ...(doctors || [])]) {
      if (!p || !p.id || !p.name || seen.has(p.id)) continue;
      seen.add(p.id);
      out.push({ id: p.id, name: p.name });
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [allCrnas, allDoctors, crnas, doctors]);

  const reset = () => {
    setPhase(IDLE);
    setError("");
    setDraftRows([]);
    setGridFlags([]);
    setSkipped([]);
    setUnassigned([]);
    setMdCall([]);
    setParsedRows([]);
    setSheetRooms([]);
    setCoverage(null);
    resetReview();
    setSourceMeta({ filename: "", source: "manual-excel" });
    editedCellsRef.current = {};
    if (fileRef.current) fileRef.current.value = "";
  };

  // Re-import a recent file: restore its saved draft snapshot straight into the
  // REVIEW phase so the AIC can eyeball it and re-apply in one click.
  const handleReimport = (rec) => {
    const s = (rec && rec.snapshot) || {};
    setError("");
    // Legacy/global records without a stored draft can't be restored here.
    if (!Array.isArray(s.draftRows) || s.draftRows.length === 0) {
      setError("This record has no saved draft snapshot to restore — run a fresh import instead.");
      return;
    }
    setDraftRows(s.draftRows || []);
    setGridFlags(s.gridFlags || []);
    setSkipped(s.skipped || []);
    setUnassigned(s.unassigned || []);
    setMdCall(s.mdCall || []);
    setParsedRows(s.parsedRows || []);
    setSheetRooms(s.sheetRooms || []);
    setCoverage(s.coverage || null);
    resetReview();
    setSourceMeta({ filename: rec?.filename || "", source: rec?.source || "manual-excel" });
    editedCellsRef.current = {};
    setPhase(REVIEW);
  };

  // ── Shared ingest ───────────────────────────────────────────────────────
  // Both sources (Excel upload, Google Sheet) hand the raw 2D `values` array
  // here. Parses the selected facility's rooms (roomFamilyForFacility) and
  // builds the reviewable DRAFT — nothing writes until the AIC confirms.
  // `sourceLabel` tailors the empty-result message. Returns true on success
  // (REVIEW), false otherwise.
  // This facility's own room-family pattern (blocker #4), if it configured one —
  // threaded to the resolver so a non-AdventHealth campus parses its own rooms.
  const roomFamilyConfig = { configByFacility: { [facilityId]: facility } };
  const ingestValues = (values, sourceText, meta, ctx = {}) => {
    // MD call list spans EVERY hospital's attending column (red=WC, blue=other).
    setMdCall(parseAttendingCallList(values));
    const { found, rows } = parsePrelimAssignments(values, {
      roomFamily: roomFamilyForFacility(facilityId, roomFamilyConfig),
    });
    if (!found || rows.length === 0) {
      setPhase(IDLE);
      // Diagnostic: OAuth + fetch can succeed yet still read a sheet/tab with no
      // Wesley Chapel rows (wrong daily file, non-first tab, or a shifted column
      // layout). Surface what was actually read so the failure is identifiable
      // without opening devtools.
      const rowCount = Array.isArray(values) ? values.length : 0;
      const roomSample = (Array.isArray(values) ? values : [])
        .map((r) => (r && r[0] != null ? String(r[0]).trim() : ""))
        .filter(Boolean)
        .slice(0, 12);
      const fileNote = meta?.readFileName ? ` Read file: "${meta.readFileName}".` : "";
      const famLabel = facilityLabel(facilityId);
      setError(
        `No ${famLabel} rooms found in ${sourceText} (room filter: ${roomFamilyForFacility(facilityId, roomFamilyConfig)}).` +
          fileNote +
          ` Read ${rowCount} row(s); first room-column values: ${roomSample.length ? roomSample.join(", ") : "(all blank)"}.` +
          ` If those aren't the expected ${famLabel} rooms, the wrong file or tab was read.`
      );
      return false;
    }
    // Match against the FULL staff pools (not today's roster) — the sheet names
    // anyone scheduled, rostered or not. Col C carries surgeon NAMES (not codes),
    // so no surgeon lookup list is passed (resolveSurgeon falls back to the name).
    const matchDoctors = allDoctors.length ? allDoctors : doctors;
    const matchCrnas = allCrnas.length ? allCrnas : crnas;
    // ⚠️ EVERY NAME GOES THROUGH THE LADDER, WITH THE ANSWERS HUMANS ALREADY
    // GAVE. The alias rung is what makes this converge instead of hover; the
    // QGenda rung is the department's own map ("Chip Compton" is Kang Compton),
    // which no nickname dictionary could ever hold.
    const identity = {
      aliases: ctx.aliases || aliases,
      qgenda: ctx.qgenda !== undefined ? ctx.qgenda : qgenda,
      facilityId,
    };
    const grid = toSheetDraftGrid(rows, {
      doctors: matchDoctors, crnas: matchCrnas, surgeons: [], rooms,
      identity, sheetValues: values,
    });
    const { rows: draft, skipped: skip, gridFlags: flags, unassigned: bench } = grid;
    const cov = facilityCoverage(grid, matchCrnas, facilityId);
    if (draft.length === 0 && (bench || []).length === 0) {
      setPhase(IDLE);
      setError(
        `All ${rows.length} rows were skipped — no matching OR room. ` +
          "Check today's room setup."
      );
      return false;
    }
    setDraftRows(draft);
    setGridFlags(flags);
    setSkipped(skip);
    setUnassigned(bench || []);
    // Keep what Apply needs to re-resolve after adding new staff.
    setParsedRows(rows);
    setSheetRooms(rooms);
    setCoverage(cov);
    resetReview();
    if (meta) setSourceMeta({ filename: meta.filename || "", source: meta.source || "manual-excel" });
    editedCellsRef.current = {};
    setPhase(REVIEW);
    return true;
  };

  // ── Google Sheet import ─────────────────────────────────────────────────
  // Resolves today's "Prelim Anes Assignments" file (Drive title lookup, newest
  // match) via an in-browser GIS OAuth popup (read-only Sheets + Drive-metadata).
  const handleSheetImport = async () => {
    setError("");
    setSkipped([]);
    setPhase(PARSING);
    try {
      const { titlePrefix, sheetId, tab } = facility?.sheetImportConfig || {};
      const { values, file, qgendaValues } = await importSheetValues({
        spreadsheetId: sheetId || undefined,
        titlePrefix: titlePrefix || undefined,
        tab: tab || undefined,
      });
      // ⚠️ THE TAB IS FETCHED BY NAME ALONGSIDE THE ASSIGNMENTS. The workbook
      // has nine tabs and an unnamed range reads the blank template.
      const qg = qgendaValues ? qgendaDisplayIndex(parseQgendaIdentity(qgendaValues)) : null;
      const known = await listStaffAliases(facilityId).catch(() => []);
      setQgenda(qg);
      setAliases(known);
      // Diagnostic boundary log: what the Drive lookup picked + what the Sheets
      // read returned. Surfaces wrong-file / wrong-tab / empty-range failures
      // (the OAuth+fetch can succeed yet still read a sheet with no WC rooms).
      console.log(
        "[AssignmentImport] sheet read:",
        { file: file?.name || "(fixed id)", rows: Array.isArray(values) ? values.length : 0 },
        "first room-column values:",
        (Array.isArray(values) ? values : []).map((r) => (r && r[0] != null ? String(r[0]).trim() : "")).filter(Boolean).slice(0, 20)
      );
      ingestValues(values, "today's Prelim Anes Assignments sheet", {
        filename: importFilename(file?.name || "Prelim Anes Assignments", date),
        source: "manual-sheet",
        readFileName: file?.name || "",
      }, { aliases: known, qgenda: qg });
    } catch (err) {
      console.error("[AssignmentImport] sheet import failed:", err);
      setPhase(IDLE);
      setError("Google Sheet import failed — " + (err?.message || "unknown error") + ".");
    }
  };

  // ── Excel file import ───────────────────────────────────────────────────
  // Parses an uploaded .xlsx/.xls (the daily sheet exported to Excel) entirely
  // in the browser via SheetJS — no upload, no AI, no Cloud Function.
  const handleExcelImport = async () => {
    setError("");
    const file = fileRef.current?.files?.[0];
    if (!file) { setError("Please select an Excel file."); return; }
    if (!EXCEL_EXT_RE.test(file.name)) {
      setError("Only Excel files (.xlsx, .xls) are supported.");
      return;
    }
    if (file.size > MAX_BYTES) {
      setError("File is too large (max 32 MB). Check the file and try again.");
      return;
    }

    setSkipped([]);
    setPhase(PARSING);
    try {
      const values = await readExcelValues(file);
      // An uploaded workbook carries no sidecar tab we can address by name, so
      // the QGenda rung is simply absent — the ladder drops one rung and the
      // review queue grows, visibly, in the coverage line.
      const known = await listStaffAliases(facilityId).catch(() => []);
      setAliases(known);
      ingestValues(values, "the Excel file", {
        filename: importFilename(file.name, date),
        source: "manual-excel",
      }, { aliases: known, qgenda: null });
    } catch (err) {
      console.error("[AssignmentImport] excel import failed:", err);
      setPhase(IDLE);
      setError(
        "Excel import failed — " +
          (err?.message || "unknown error") +
          ". Check the file or enter manually."
      );
    }
  };

  const handleCellChange = (rowIndex, field, value) => {
    // Track the edit so the Apply-time new-staff re-resolve can re-apply it —
    // without this, re-resolving from raw parsedRows would revert the AIC's
    // reviewed corrections (WR-05).
    editedCellsRef.current[rowIndex] = {
      ...editedCellsRef.current[rowIndex],
      [field]: value,
    };
    setDraftRows((prev) =>
      prev.map((row, i) => (i === rowIndex ? { ...row, [field]: value } : row))
    );
  };

  // The gate. An import with open questions cannot be applied until somebody
  // says they read them — Apply rewrites the day.
  const applyBlocked = applyBlockedBy(coverage, acknowledged);

  const handleApply = async () => {
    if (typeof onApplyRows !== "function") return;
    setPhase(APPLYING);
    try {
      // ⚠️ NO STAFF ARE CREATED HERE, SO THERE IS NOTHING TO RE-RESOLVE AGAINST.
      // This block used to create every unmatched name and then re-run the draft
      // so the fresh ids linked. Confirmations now happen in the queue ABOVE,
      // against the live alias store, and a re-parse there is a re-import — one
      // click, on data a person has already looked at.
      let appliedRows = draftRows;
      const appliedBench = unassigned;
      await onApplyRows(appliedRows, { unassigned: appliedBench, mdCall });
      // Record this applied import in the recent-files history (best-effort —
      // a history-write failure must not fail the apply the AIC just confirmed).
      if (typeof onSaveImport === "function") {
        try {
          await onSaveImport({
            filename: sourceMeta.filename || importFilename("assignment-sheet.xlsx", date),
            source: sourceMeta.source || "manual-excel",
            snapshot: {
              draftRows: appliedRows,
              gridFlags,
              skipped,
              unassigned: appliedBench,
              mdCall,
              parsedRows,
              sheetRooms,
              coverage,
            },
          });
        } catch (saveErr) {
          console.error("[AssignmentImport] save recent import failed:", saveErr);
        }
      }
      reset();
    } catch (err) {
      console.error("[AssignmentImport] apply failed:", err);
      setPhase(REVIEW);
      setError("Apply failed — " + (err?.message || "unknown error") + ". Check your connection.");
    }
  };

  // ── Render: idle ──
  if (phase === IDLE) {
    const nowMs = Date.now();
    return (
      <Card style={{ marginBottom: space.md }}>
        <div style={st.header}>
          <span style={st.headerTitle}>Import Excel Assignment Sheet</span>
          <span style={st.headerNote}>
            Upload the day's Excel file (.xlsx) to pre-fill the grid for review before applying.
          </span>
        </div>
        {error && (
          <div role="alert" aria-live="polite" style={st.errorBanner}>
            {error}
          </div>
        )}
        <div style={st.uploadRow}>
          <label htmlFor="ai-import-file" style={st.fileLabel}>
            Select Excel file (.xlsx)
          </label>
          <input
            id="ai-import-file"
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.xlsm,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
            style={st.fileInput}
            aria-label="Select assignment sheet Excel file"
          />
          <Button onClick={handleExcelImport}>Import Excel</Button>
        </div>
        <div style={st.sheetRow}>
          <span style={st.headerNote}>
            Or pull today's {facilityLabel(facilityId)} rooms directly from the Prelim Anes
            Assignments Google Sheet. <strong>This facility only</strong> — the global
            all-facilities import is the 🌐 panel below (admins).
          </span>
          <Button variant="secondary" onClick={handleSheetImport}>
            Import from Google Sheet — {facilityLabel(facilityId)} only
          </Button>
        </div>

        {/* Recent imports — last 3 applied sheets; click to re-review + re-apply. */}
        {recentImports.length > 0 && (
          <div style={st.recentWrap}>
            <div style={st.recentTitle}>Recent imports</div>
            <div style={st.recentList}>
              {recentImports.map((rec) => (
                <button
                  key={rec.id}
                  type="button"
                  style={st.recentItem}
                  onClick={() => handleReimport(rec)}
                  aria-label={`Re-import ${rec.filename || "sheet"}`}
                >
                  <span style={st.recentItemMain}>
                    <span style={st.recentName}>{rec.filename || "Imported sheet"}</span>
                    <span style={st.recentMeta}>
                      {rec.dateKey || ""}
                      {relativeImportTime(rec, nowMs) ? ` · ${relativeImportTime(rec, nowMs)}` : ""}
                    </span>
                  </span>
                  <span style={st.recentRight}>
                    <span style={st.recentBadge}>{sourceLabel(rec.source)}</span>
                    <span style={st.recentReimport}>Re-import →</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </Card>
    );
  }

  // ── Render: parsing ──
  if (phase === PARSING) {
    return (
      <Card style={{ marginBottom: space.md }}>
        <div
          role="status"
          aria-live="polite"
          aria-busy="true"
          style={st.spinnerRow}
        >
          <span style={st.spinner} aria-hidden="true">&#9696;</span>
          <span style={st.spinnerLabel}>Parsing sheet…</span>
        </div>
      </Card>
    );
  }

  // ── Render: applying ──
  if (phase === APPLYING) {
    return (
      <Card style={{ marginBottom: space.md }}>
        <div role="status" aria-live="polite" aria-busy="true" style={st.spinnerRow}>
          <span style={st.spinner} aria-hidden="true">&#9696;</span>
          <span style={st.spinnerLabel}>Applying to board…</span>
        </div>
      </Card>
    );
  }

  // ── Render: review draft ──
  const aicFlag = gridFlags.find((f) => f.kind === "aicTag");
  const flaggedCount = draftRows.filter((r) => r.flags && r.flags.length > 0).length;

  return (
    <Card style={{ marginBottom: space.md }}>
      <div style={st.header}>
        <span style={st.headerTitle}>Review Imported Draft</span>
        <span style={st.headerNote}>
          All fields are editable. Review flags before applying.
        </span>
      </div>

      {/* Grid-level AIC flag */}
      {aicFlag && (
        <div role="alert" aria-live="polite" style={st.aicWarning}>
          {aicFlag.count === 0
            ? "No AIC tag found — set the AIC before applying."
            : `${aicFlag.count} AIC tags found — exactly one is required. Correct before applying.`}
        </div>
      )}

      {/* Per-row flag summary */}
      {flaggedCount > 0 && (
        <div role="status" aria-live="polite" style={st.flagSummary}>
          {flaggedCount} row{flaggedCount > 1 ? "s have" : " has"} flags — review highlighted rows.
        </div>
      )}

      {/* Skipped rows (sheet import) — unknown OR room or TBD placeholder */}
      {skipped.length > 0 && (
        <div role="status" aria-live="polite" style={st.flagSummary}>
          Skipped {skipped.length} row{skipped.length > 1 ? "s" : ""}:{" "}
          {skipped.map((s) => `${s.room} (${s.reason === "tbdRoom" ? "TBD placeholder" : "no matching room"})`).join(", ")}
        </div>
      )}

      {/* Unassigned bench — CRNAs parked on a TBD room; added to the roster
          unassigned (not a room) on Apply. */}
      {unassigned.length > 0 && (
        <div role="status" aria-live="polite" style={st.flagSummary}>
          Unassigned (to roster, no room): {unassigned.map((u) => u.name).join(", ")}
        </div>
      )}

      {error && (
        <div role="alert" aria-live="polite" style={st.errorBanner}>
          {error}
        </div>
      )}

      {/* ⚠️ THE COVERAGE REPORT AND THE QUESTIONS, ABOVE THE DRAFT TABLE AND
          ABOVE THE APPLY BUTTON. What used to sit here was a list of names with
          every "add this person" box already ticked. */}
      <ImportReviewQueue
        coverage={coverage} resolvedIds={answered}
        acknowledged={acknowledged} onAcknowledge={setAcknowledged}
        onConfirm={confirmAlias} onCreatePerson={createPerson} onSkip={skipItem}
        staffOptions={reviewStaffOptions}
      />

      {/* Draft review table */}
      <div style={st.tableWrap}>
        <table style={st.table} aria-label="Imported draft assignments">
          <thead>
            <tr style={st.headRow}>
              <th scope="col" style={st.th}>Room</th>
              <th scope="col" style={st.th}>Start</th>
              <th scope="col" style={st.th}>Attending</th>
              <th scope="col" style={st.th}>CRNA / AHP</th>
              <th scope="col" style={st.th}>Shift</th>
            </tr>
          </thead>
          <tbody>
            {draftRows.map((row, i) => (
              <DraftRow
                key={i}
                row={row}
                index={i}
                onChange={handleCellChange}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Actions */}
      <div style={st.actions}>
        <Button variant="secondary" onClick={reset}>
          Cancel — use manual grid
        </Button>
        <Button onClick={handleApply} disabled={Boolean(applyBlocked)}>
          {applyBlocked
            ? `Read the ${applyBlocked} open item${applyBlocked === 1 ? "" : "s"} first`
            : "Confirm / Apply to board"}
        </Button>
      </div>
    </Card>
  );
}
